import React from "react";
import { Grid, TextField, Typography } from "@mui/material";
import { SectionContainer, MainContainer, DetailsBlock, DifferenceAdditionalText } from "./styles";
import AuditNotes from "../../AuditNotes/AuditNotes";
import { EmployeeRef } from "../../../../../../../../../../models/employees";
import { TravelExpenseRef } from "../../../../../../../../../../models/travel-expenses";
import { TextFieldTypeAmount } from "../../../../../../../../../../reusable-components/Table/AddEditItem/TextFieldTypeAmount/TextFieldTypeAmount";
import ReadOnlyReceiptDetails from "../../../ReceiptComponents/ReadOnlyReceiptDetails/ReadOnlyReceiptDetails";
import AuditorCommentsFields from "./AuditorCommentsFields/AuditorCommentsFields";
import { DocumentDTO } from "../../../../../../../../../../models/documents";
import { TravelExpenseAuditNotesDTO } from "../../../../../../../../../../models/travel-expense-audit-notes";
import { TravelExpenseDTO } from "src/models/travel-expenses/travel-expense-dto";
import { useTranslation } from "react-i18next";

interface Props {
  travelExpense: TravelExpenseRef;
  readOnly: boolean;
  employee: EmployeeRef;
  updateAuditDoc: (document: DocumentDTO | null) => void;
  updateLastAuditNote: (auditNote: TravelExpenseAuditNotesDTO) => void;
  defaultTravelExpense: TravelExpenseRef;
  handleChangeField: (field: keyof TravelExpenseDTO, value: TravelExpenseDTO[keyof TravelExpenseDTO]) => void;
  handleUpdateExportAuditNotes?: (travelExpenseId: string, include: boolean) => void;
}

const AuditCommentsBody = ({
  travelExpense,
  employee,
  updateAuditDoc,
  updateLastAuditNote,
  defaultTravelExpense,
  readOnly,
  handleChangeField,
  handleUpdateExportAuditNotes,
}: Props) => {
  const tRP = "components.add_edit_travel_expense";
  const { t } = useTranslation();

  return (
    <MainContainer container>
      <SectionContainer
        style={{
          position: "sticky",
          top: 0,
        }}
      >
        <DetailsBlock>
          <>
            {!readOnly && (
              <>
                <ReadOnlyReceiptDetails
                  travelExpense={travelExpense}
                  withAuditorEditableFields={false}
                />

                <TextFieldTypeAmount
                  field={"vatAmount"}
                  labelKey={`${t(`${tRP}.fields.vat_amount`)}`}
                  amountValue={travelExpense.vatAmount ?? 0}
                  handleChange={(_, number) => handleChangeField("vatAmount", number)}
                  endAdornment={<Typography>{travelExpense.convertedAmountCurrency?.code ?? ""}</Typography>}
                  error={travelExpense.vatAmount > travelExpense.amount}
                  helperText={
                    travelExpense.vatAmount > travelExpense.amount ? "VAT amount greater than submitted amount" : ""
                  }
                />

                <TextField
                  label={`${t(`${tRP}.fields.vat_rate`)}`}
                  type="number"
                  value={travelExpense.vatRate}
                  onChange={(e) => {
                    if (!!Number(e.target.value || Number(e.target.value) === 0)) {
                      Number(e.target.value) >= 0 &&
                        Number(e.target.value) <= 100 &&
                        handleChangeField("vatRate", e.target.value);
                    } else {
                      handleChangeField("vatRate", e.target.value);
                    }
                  }}
                  InputProps={{
                    inputProps: { min: 0, max: 100, step: 0.1 },
                    endAdornment: <Typography>%</Typography>,
                  }}
                />

                <TextFieldTypeAmount
                  field={"acceptedAmount"}
                  labelKey={`${t(`${tRP}.fields.accepted_amount`)}`}
                  amountValue={travelExpense.acceptedAmount ?? 0}
                  handleChange={(_, number) => handleChangeField("acceptedAmount", number)}
                  endAdornment={<Typography>{travelExpense.currency?.code ?? ""}</Typography>}
                  error={travelExpense.acceptedAmount > travelExpense.amount}
                  helperText={
                    travelExpense.acceptedAmount > travelExpense.amount
                      ? `${t(`${tRP}.accepted_amount_greater_than_submitted`)}`
                      : ""
                  }
                />

                <Grid sx={{ width: "100%", "& div": { width: "100%" } }}>
                  <TextFieldTypeAmount
                    field={"difference"}
                    labelKey={`${t(`${tRP}.fields.difference`)}`}
                    amountValue={travelExpense.difference ?? 0}
                    handleChange={(_, number) => handleChangeField("difference", number)}
                    endAdornment={<Typography>{travelExpense.currency?.code ?? ""}</Typography>}
                  />
                  <DifferenceAdditionalText variant="body2">
                    {`${t(`${tRP}.fields.amount_cannot_be_reimbursed`)}`}
                  </DifferenceAdditionalText>
                </Grid>

                {/* <AuditDocument
                  travelExpense={travelExpense}
                  document={travelExpense.auditDocument}
                  employee={employee}
                  handleNotifyParent={updateAuditDoc}
                  readOnly={readOnly}
                  customTitleStyle={readOnly ? {} : { fontWeight: "bold", color: "inherit" }}
                /> */}
              </>
            )}

            {readOnly && (
              <>
                <ReadOnlyReceiptDetails travelExpense={travelExpense} />

                {/* <AuditorCommentsFields
                  readOnly={readOnly}
                  travelExpense={travelExpense}
                  handleChangeField={handleChangeField}
                /> */}
              </>
            )}
          </>
        </DetailsBlock>
      </SectionContainer>

      <SectionContainer>
        <DetailsBlock>
          {readOnly && (
            <>
              {/* <ReadOnlyReceiptDetails travelExpense={travelExpense} /> */}

              <AuditorCommentsFields
                readOnly={readOnly}
                travelExpense={travelExpense}
                handleChangeField={handleChangeField}
              />
            </>
          )}
          <>
            {!readOnly && (
              <AuditorCommentsFields
                readOnly={readOnly}
                travelExpense={travelExpense}
                handleChangeField={handleChangeField}
              />
            )}
          </>

          <>
            {/* {readOnly && (
              <AuditDocument
                travelExpense={travelExpense}
                document={travelExpense.auditDocument}
                employee={employee}
                handleNotifyParent={updateAuditDoc}
                readOnly={readOnly}
                customTitleStyle={readOnly ? {} : { fontWeight: "bold", color: "inherit" }}
              />
            )} */}
          </>

          <AuditNotes
            employee={employee}
            travelExpense={defaultTravelExpense}
            legalEntity={employee?.legalEntity ?? defaultTravelExpense?.legalEntity}
            readOnly={readOnly}
            updateLastAuditNote={updateLastAuditNote}
            handleUpdateExportAuditNotes={handleUpdateExportAuditNotes}
          />
        </DetailsBlock>
      </SectionContainer>
    </MainContainer>
  );
};

export default AuditCommentsBody;
